import Box from '@zenbusiness/zui/Box';
import IconButton from '@zenbusiness/zui/IconButton';
import superstyled from '@zenbusiness/zui/styles/superstyled';
import TextField from '@zenbusiness/zui/TextField';

export const InputRoot = superstyled({
  name: 'InputRoot',
  slot: 'Root',
  tag: TextField,
  styles: ({ theme }) => ({
    position: 'sticky',
    width: '100%',
    '& .MuiInputBase-root': {
      padding: 0,
      border: 'none', // Remove any border
      '&:before, &:after': {
        display: 'none' // Remove standard variant underline
      },
      '&:hover:not(.Mui-disabled):before': {
        display: 'none' // Remove hover underline
      },
      '&.Mui-focused:after': {
        display: 'none' // Remove focus underline
      }
    },
    '& .MuiInputBase-input': {
      paddingBlock: theme.spacing(1),
      paddingInline: 0,
      fontSize: theme.typography.body1.fontSize,
      lineHeight: theme.typography.body1.lineHeight,
      resize: 'none',
      border: 'none', // Remove any border from the input itself
      outline: 'none', // Remove focus outline

      // Placeholder styling for normal state
      '&::placeholder': {
        color: theme.palette.grey.light,
        opacity: 1
      },

      // Placeholder styling when disabled/processing - make it more grey
      '&:disabled::placeholder': {
        color: theme.palette.text.disabled,
        opacity: 0.7
      }
    },
    '& .MuiFormLabel-root': {
      display: 'none' // Hide label since we're using placeholder
    },
    // Remove any fieldset borders (in case they exist)
    '& fieldset': {
      border: 'none'
    }
  })
});

export const MainContainer = superstyled({
  name: 'MainContainer',
  slot: 'MainContainer',
  tag: Box,
  styles: () => ({
    display: 'flex',
    width: '100%'
  })
});

export const GradientContainer = superstyled({
  name: 'GradientContainer',
  slot: 'GradientContainer',
  tag: Box,
  propsNeeded: ['active', 'processing'],
  styles: ({ theme }) => ({
    position: 'relative',
    display: 'grid',
    gridTemplateColumns: '2.5rem 1fr 2.5rem',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    paddingInline: theme.spacing(1.25, 2),
    paddingBlock: theme.spacing(1),
    borderRadius: theme.shape.borderRadius * 3,
    width: '100%',
    backgroundColor: 'white'
  })
});

export const ControlsContainer = superstyled({
  name: 'ControlsContainer',
  slot: 'ControlsContainer',
  tag: Box,
  styles: () => ({
    // display: 'flex',
    // justifyContent: 'space-between',
    // alignItems: 'center',
    // padding: theme.spacing(1.5)
  })
});

export const InputContainer = superstyled({
  name: 'InputContainer',
  slot: 'InputContainer',
  tag: Box,
  styles: () => ({
    // display: 'flex',
    // justifyContent: 'space-between',
    // alignItems: 'center',
    // padding: theme.spacing(3, 3, 1, 3)
  })
});

export const GradientShadow = superstyled({
  name: 'GradientShadow',
  slot: 'GradientShadow',
  tag: Box,
  styles: ({ theme }) => ({
    background: `linear-gradient(270deg, #3971E7 0%, #33F28B 100%)`,
    borderRadius: theme.shape.borderRadius * 3,
    display: 'block',
    position: 'absolute',
    top: 10,
    left: 10,
    right: 10,
    bottom: -2,
    filter: 'blur(6px)',
    zIndex: -1
  })
});

export const SendStopButton = superstyled({
  name: 'SendStopButton',
  slot: 'SendStopButton',
  tag: IconButton,
  propsNeeded: ['bgcolor'],
  styles: ({ bgcolor }) => ({
    color: 'grey',
    cursor: 'pointer',
    padding: 0,

    '& .MuiButton-startIcon': {
      fontSize: '2rem',
      backgroundColor: bgcolor,
      borderRadius: '50%',
      padding: '0.25rem'
    },
    // if you need to go deeper and hit the SVG directly:
    '& .MuiButton-startIcon .MuiSvgIcon-root': {
      fontSize: '1.8rem'
    },
    '&:hover': {
      backgroundColor: 'transparent'
    }
  })
});

export const ProcessingText = superstyled({
  name: 'ProcessingText',
  slot: 'ProcessingText',
  tag: Box,
  styles: () => ({
    flexGrow: 1,
    color: 'gray'
  })
});

export const AvatarContainer = superstyled({
  name: 'AvatarContainer',
  slot: 'AvatarContainer',
  tag: Box,
  styles: () => ({
    alignSelf: 'end'
  })
});

export const ButtonContainer = superstyled({
  name: 'ButtonContainer',
  slot: 'ButtonContainer',
  tag: Box,
  styles: () => ({
    alignSelf: 'end',
    paddingBottom: '0.4rem'
  })
});

export const MoreIndicator = superstyled({
  name: 'MoreIndicator',
  slot: 'MoreIndicator',
  tag: Box,
  propsNeeded: ['visible'],
  styles: ({ visible, theme }) => ({
    position: 'sticky', // stick to the bottom of the scroll container
    bottom: 0,
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-end',
    alignSelf: 'stretch',
    padding: theme.spacing(1.5, 0, 1.5, 0),

    background: `linear-gradient(182deg, rgba(250, 250, 250, 0.12) 1.87%, rgba(250, 250, 250, 0.96) 50.12%)`,
    boxShadow: '0 0 -50px 50px #fafafa99',

    transform: visible ? 'translateY(0)' : 'translateY(-100%)', // slide in/out vertically
    opacity: visible ? 1 : 0,
    transition: `
      all 300ms ${theme.transitions.easing.easeOut}`,
    zIndex: 5, // above your input bar
    pointerEvents: visible ? 'auto' : 'none',
    '& .MuiSvgIcon-root': {
      marginRight: theme.spacing(1),
      fontSize: '1.2rem',
      color: theme.palette.text.secondary
    },
    '& .MoreIndicator-text': {
      fontSize: '0.875rem',
      color: theme.palette.text.secondary
    }
  })
});
